import { Injectable, Inject, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { IAuthApi, TokenDataGetter, TokenData } from './auth.model';
import {
  TOKEN_DATA_GETTER,
  API_LOGIN_ENDPOINT,
  API_REFRESH_ENDPOINT,
  API_LOGOUT_ENDPOINT
} from './auth.constant';
import {
  TOTPRegistrationStartResponse,
  TOTPRegistrationFinishRequest,
  WebAuthnRegistrationStartResponse,
  WebAuthnRegistrationFinishRequest,
  WebAuthnLoginStartResponse,
  WebAuthnLoginFinishRequest,
  Credential,
  SudoStatusResponse,
  WebAuthnSudoRequest,
  TOTPLoginResponse,
  RecoveryCodesResponse
} from '@zerops/models/auth';

interface DefaultResponse {
  auth: any;
  user: any;
}

@Injectable({ providedIn: 'root' })
export class ZefAuthApi implements IAuthApi {
  #http = inject(HttpClient);

  constructor(
    @Inject(TOKEN_DATA_GETTER)
    private _tokenDataGetter: TokenDataGetter,
    @Inject(API_LOGIN_ENDPOINT)
    private _apiLoginEndpoint: string,
    @Inject(API_REFRESH_ENDPOINT)
    private _apiRefreshEndpoint: string,
    @Inject(API_LOGOUT_ENDPOINT)
    private _apiLogoutEndpoint: string
  ) { }

  // # Core Auth Endpoints
  login$(data: any) {
    return this.#http
      .post<DefaultResponse>(
        this._apiLoginEndpoint,
        data
      )
      .pipe(
        map((response) => ({
          tokenData: this._tokenDataGetter(response),
          response
        }))
      );
  }

  logout$() {
    return this.#http.post<{ success: true }>(this._apiLogoutEndpoint, {});
  }

  refresh$<D = DefaultResponse>(refreshTokenId: string) {
    return this.#http.post<D>(
      this._apiRefreshEndpoint,
      { refreshTokenId }
    );
  }

  // # TOTP Endpoints
  totpRegistrationStart$() {
    return this.#http.post<TOTPRegistrationStartResponse>(
      '/api/2fa/totp/registration/start',
      {}
    );
  }

  totpRegistrationFinish$(data: TOTPRegistrationFinishRequest) {
    return this.#http.post<TokenData>(
      '/api/2fa/totp/registration/finish',
      data
    );
  }

  totpRemove$() {
    return this.#http.delete<void>('/api/2fa/totp');
  }

  totpLogin$(token: string) {
    return this.#http.post<TOTPLoginResponse>(
      '/api/2fa/totp/login',
      { token }
    );
  }

  // # WebAuthn Endpoints
  webAuthnRegistrationStart$(residentKey: boolean) {
    return this.#http.post<WebAuthnRegistrationStartResponse>(
      '/api/webauthn/registration/start',
      { residentKey }
    );
  }

  webAuthnRegistrationFinish$(data: WebAuthnRegistrationFinishRequest) {
    return this.#http.post<{ auth: TokenData }>(
      '/api/webauthn/registration/finish',
      data
    );
  }

  // # Credentials Endpoints
  credentialsList$() {
    return this.#http.get<{ list: Credential[] }>(
      '/api/2fa/credentials/list'
    );
  }

  credentialDelete$(id: string) {
    return this.#http.delete<void>(`/api/2fa/credentials/${id}`);
  }

  // # Recovery Codes Endpoints
  recoveryCodesGet$() {
    return this.#http.get<RecoveryCodesResponse>(
      '/api/2fa/recovery-codes'
    );
  }

  recoveryCodesGenerate$() {
    return this.#http.post<RecoveryCodesResponse>(
      '/api/2fa/recovery-codes/regenerate',
      {}
    );
  }

  // # 2FA Login Flow Endpoints
  twoFALoginStart$() {
    return this.#http.post<WebAuthnLoginStartResponse>(
      '/api/2fa/login/start',
      {}
    );
  }

  twoFALoginFinish$(data: WebAuthnLoginFinishRequest) {
    return this.#http.post<{ auth: TokenData }>(
      '/api/2fa/login/finish',
      data
    );
  }

  // # Passkey Login Flow Endpoints
  passkeyLoginStart$() {
    return this.#http.post<WebAuthnLoginStartResponse>(
      '/api/webauthn/login/start',
      {}
    );
  }

  passkeyLoginFinish$(data: WebAuthnLoginFinishRequest) {
    return this.#http.post<{ auth: TokenData }>(
      '/api/webauthn/login/finish',
      data
    );
  }

  // # Sudo Mode Endpoints
  sudoStatus$() {
    return this.#http.get<SudoStatusResponse>('/api/auth/sudo');
  }

  sudoEnablePassword$(password: string) {
    return this.#http.post<TokenData>('/api/auth/sudo', { password });
  }

  sudoEnableTOTP$(token: string) {
    return this.#http.post<TokenData>('/api/auth/sudo', { token });
  }

  sudoEnableWebAuthn$(webAuthn: WebAuthnSudoRequest) {
    return this.#http.post<TokenData>('/api/auth/sudo', { webAuthn });
  }

  // # Password Management Endpoints
  /**
   * Change user password.
   * Set newPassword to null to remove password authentication.
   */
  changePassword$(data: { oldPassword: string; newPassword: string | null }) {
    return this.#http.put<any>('/api/user/password', data);
  }

}
